import type { ReactNode } from 'react';
import { dialogStore } from './dialogStore';

/**
 * Imperatively show a dialog that resolves to a value or to `undefined`
 * (cancel). The `dialog` builder receives `ok(value)` and `cancel()`
 * callbacks and returns the JSX to render — typically a `<Modal>` from
 * react-bootstrap (or anything else) with buttons wired to these
 * callbacks.
 *
 * @example
 * ```ts
 * const confirmed = await waitDialog<boolean>((ok, cancel) => (
 *   <ConfirmModal
 *     title="Delete?"
 *     onConfirm={() => ok(true)}
 *     onCancel={cancel}
 *   />
 * ));
 * if (confirmed) doDelete();
 * ```
 */
export function waitDialog<T>(dialog: (ok: (value: T) => void, cancel: () => void) => ReactNode): Promise<T | undefined> {
  return new Promise((resolve) => {
    let close = (): void => {};
    const ok = (value: T): void => {
      close();
      resolve(value);
    };
    const cancel = (): void => {
      close();
      resolve(undefined);
    };
    close = dialogStore.show(dialog(ok, cancel)).close;
  });
}

/**
 * Show a dialog with no return value — fire-and-forget. The `dialog`
 * builder receives a `close()` callback to dismiss the dialog when the
 * user is done.
 */
export function showDialog(dialog: (close: () => void) => ReactNode): void {
  let close = (): void => {};
  const userClose = (): void => close();
  close = dialogStore.show(dialog(userClose)).close;
}
